// Maps a live-notification scope (see live-socket.ts's LiveServerFrame) to
// the TanStack Query cache entries it should invalidate. Uses the exact
// predicate pattern documented in front/CLAUDE.md — matching the raw
// `['get', path, {params}]` query key shape openapi-react-query produces —
// rather than the naive `const [method, path] = query.queryKey` destructure
// that front/CLAUDE.md explicitly calls out as wrong (it happens to work by
// accident for exact 2-tuples, but silently breaks for the common case
// where a third `{params}` element carries the org/entity scoping this file
// depends on).
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { LiveScope } from './live-socket'

type QueryKeyParams = { path?: Record<string, string | undefined> }

function paramsOf(queryKey: QueryKey): QueryKeyParams | undefined {
    const key = queryKey as unknown[]
    const entry = key.find((item): item is { params: QueryKeyParams } =>
        typeof item === 'object' && item !== null && 'params' in item
    )
    return entry?.params
}

// isGetQueryFor reports whether queryKey is an openapi-react-query GET query
// key for exactly `path` (e.g. '/api/v1/organizations/{orgCode}/assets').
function isGetQueryFor(queryKey: QueryKey, path: string): boolean {
    const key = queryKey as unknown[]
    return key.length >= 2 && key[0] === 'get' && key[1] === path
}

// invalidateOrgScoped invalidates every GET query for `path` whose
// `params.path.orgCode` matches `org` — the coarse "any change in this org
// invalidates every mounted query for this resource" shape used for scopes
// where per-row precision isn't worth the complexity (see the `properties`
// case in docs/apis/events.md).
function invalidateOrgScoped(queryClient: QueryClient, path: string, org: string) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            if (!isGetQueryFor(query.queryKey, path)) return false
            return paramsOf(query.queryKey)?.path?.orgCode === org
        },
    })
}

// invalidateOrgAndKeyScoped invalidates every GET query for `path` whose
// `params.path.orgCode` AND `params.path[idParam]` both match — used for
// detail-scope hints where a specific asset/document/etc uid is known, so
// only that one entity's cached queries refetch instead of every mounted
// query for the resource.
function invalidateOrgAndKeyScoped(queryClient: QueryClient, path: string, org: string, idParam: string, idValue: string) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            if (!isGetQueryFor(query.queryKey, path)) return false
            const params = paramsOf(query.queryKey)?.path
            return params?.orgCode === org && params?.[idParam] === idValue
        },
    })
}

// invalidatePathsForEntity is the per-scope-entity dispatch table. Each
// entry lists the REST path(s) to invalidate for a collection-scope hint
// (uid undefined) and, where the entity supports a detail scope, the
// additional path(s) + id param name to invalidate precisely when a uid is
// known. See docs/apis/events.md's "Watchable entities" table for the full
// entity -> backing-table mapping this mirrors on the frontend side.
//
// manualKeyPrefixes covers hooks that bypass $api.useQuery's auto-generated
// key in favor of a hand-rolled `useQuery({ queryKey: [...] })` call — e.g.
// the assets LIST page's useAssets (front/src/api/queries.ts) uses
// `['assets', orgCode, assetTypeParam, q, ...]`, not the openapi-fetch
// shape. TanStack Query's invalidateQueries matches by KEY PREFIX by
// default (no `exact: true`), so invalidating just `['assets', org]`
// catches every variant of that hook's params for this org.
//
// detailManualKeyPrefixes is the detail-scope analog for entities whose
// hooks are ENTIRELY manual-key (no openapi-fetch-shaped queries at all —
// `jobs` is the first such entity, see front/src/api/jobs.ts). Applied by
// invalidateForScope only when scope.uid is set, in addition to
// manualKeyPrefixes (which always applies, uid or not).
const ENTITY_PATHS: Record<
    string,
    {
        collection: string[]
        detail?: { paths: string[]; idParam: string }
        manualKeyPrefixes?: (org: string) => QueryKey[]
        detailManualKeyPrefixes?: (org: string, uid: string) => QueryKey[]
    }
> = {
    assets: {
        collection: [
            '/api/v1/organizations/{orgCode}/assets',
            '/api/v1/organizations/{orgCode}/imports/{importUid}/summary',
            // A plan_proposals row hint carries the proposal's uid, never the
            // frame's: only the collection scope can refresh the editor's list.
            '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/proposals',
        ],
        detail: {
            paths: [
                '/api/v1/organizations/{orgCode}/assets/{assetUid}',
                '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
                '/api/v1/organizations/{orgCode}/assets/{assetUid}/picture',
            ],
            idParam: 'assetUid',
        },
        manualKeyPrefixes: (org) => [['assets', org]],
    },
    documents: {
        collection: [
            '/api/v1/organizations/{orgCode}/documents',
            '/api/v1/organizations/{orgCode}/imports/{importUid}/summary',
            // The sidebar's attention badges (spec 2026-10-27-23).
            '/api/v1/organizations/{orgCode}/users/me/attention',
        ],
        detail: {
            paths: [
                '/api/v1/organizations/{orgCode}/documents/{documentUid}',
                '/api/v1/organizations/{orgCode}/documents/{documentUid}/properties',
            ],
            idParam: 'documentUid',
        },
    },
    // folder-layouts is deliberately collection-only, mirroring the backend:
    // a document_folders hint carries the FOLDER's own uid, which would never
    // match a layout-uid detail scope (see docs/apis/events.md). Every
    // consumer keys its queries by hand rather than through $api.useQuery,
    // except the layouts LIST hook in hooks/useQueries.ts — hence one
    // openapi-shaped path plus three manual prefixes.
    'folder-layouts': {
        collection: ['/api/v1/organizations/{orgCode}/document-folders-layouts'],
        // ['documentLayouts', org] — the list page (routes/$org/folder-layouts/
        // index.tsx); ['documentLayout', org] and ['documentFolders', org] are
        // PREFIXES of the editor page's ['…', org, layoutUid] keys, so one
        // hint refreshes whichever layout is currently open.
        manualKeyPrefixes: (org) => [
            ['documentLayouts', org],
            ['documentLayout', org],
            ['documentFolders', org],
        ],
    },
    contracts: {
        // The /stakeholders and /assets sub-resources are deliberately in
        // the COLLECTION list, not detail.paths: writes to them hit the
        // contract_actors / contract_assets child tables, whose hints carry
        // the CHILD row's own uid (scope.go's child-table rule) and so only
        // ever dirty the collection scope — never a contractUid-specific
        // detail scope. invalidateOrgScoped matches every mounted query for
        // this path org-wide regardless of contractUid, which is the same
        // coarse-but-correct shape as the `properties` entry below — cheap,
        // since only an open contract page ever mounts these queries.
        collection: [
            '/api/v1/organizations/{orgCode}/contracts',
            '/api/v1/organizations/{orgCode}/imports/{importUid}/summary',
            '/api/v1/organizations/{orgCode}/contracts/{contractUid}/stakeholders',
            '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets',
            // The site contract checklist reads contracts: one created by
            // extraction turns its row covered (spec 2026-10-11-17).
            '/api/v1/organizations/{orgCode}/assets/{assetUid}/contract-checklist',
            // The sidebar's attention badges (spec 2026-10-27-23).
            '/api/v1/organizations/{orgCode}/users/me/attention',
        ],
        detail: {
            paths: ['/api/v1/organizations/{orgCode}/contracts/{contractUid}'],
            idParam: 'contractUid',
        },
    },
    issues: {
        collection: [
            '/api/v1/organizations/{orgCode}/issues',
            '/api/v1/organizations/{orgCode}/issues/stats',
            // The sidebar's attention badges (spec 2026-10-27-23).
            '/api/v1/organizations/{orgCode}/users/me/attention',
        ],
        detail: {
            paths: ['/api/v1/organizations/{orgCode}/issues/{issueUid}'],
            idParam: 'issueUid',
        },
    },
    // conversations — the resident request ledger (spec 2026-09-02-05). The
    // collection covers the queue list and its folder counters; the detail
    // scope is the open thread, which since migration 190 receives a hint
    // carrying the CONVERSATION's uid for a message, a promotion link, an
    // about-set edit or a linked issue's state — not the child row's own uid.
    conversations: {
        collection: [
            '/api/v1/organizations/{orgCode}/conversations',
            '/api/v1/organizations/{orgCode}/settings/conversations',
            // The sidebar's attention badges (spec 2026-10-27-23).
            '/api/v1/organizations/{orgCode}/users/me/attention',
        ],
        detail: {
            // The detail response embeds the thread's messages, its assets and
            // its linked issues, so this one path is the whole open thread.
            paths: ['/api/v1/organizations/{orgCode}/conversations/{conversationUid}'],
            idParam: 'conversationUid',
        },
    },
    tags: {
        // Tags have no dedicated frontend consumer yet (spec
        // 2026-07-04-02), but the whitelist already covers the backend
        // entity — wiring the invalidation now means the first tags UI
        // gets live updates for free instead of this file needing a
        // follow-up edit.
        collection: ['/api/v1/organizations/{orgCode}/tags'],
        detail: {
            paths: ['/api/v1/organizations/{orgCode}/tags/{tagUid}'],
            idParam: 'tagUid',
        },
    },
    users: {
        // The openapi-fetch-shaped `collection` entry below has no current
        // consumer: the `useUsers` hook it used to serve
        // (hooks/useQueries.ts, formerly used by CreateContractPage's
        // stakeholder picker, InterventionPage's provider picker, and
        // ExpensePage's supplier picker) was removed — those three now use
        // the API-backed UserSelect component (useUsersSearch, not
        // $api.useQuery, so no cache key to invalidate here) — see specs/
        // todos/2026-07-21-04-user-pickers-truncated-to-first-page.md.
        // Left in place as a harmless, forward-compatible whitelist entry:
        // every users PAGE (list, detail, the detail page's children list
        // and ancestor chain) uses hand-rolled query keys in api/queries.ts
        // / routes/$org/users/$userUid/index.tsx, which is what
        // manualKeyPrefixes/detailManualKeyPrefixes below cover; `detail`
        // below is still live — useUser uses $api.useQuery for that path.
        // The import summary counts parties (users) created by the import, so
        // a users hint refetches it too (spec 2026-09-30-11).
        collection: [
            '/api/v1/organizations/{orgCode}/users',
            '/api/v1/organizations/{orgCode}/imports/{importUid}/summary',
        ],
        detail: {
            paths: ['/api/v1/organizations/{orgCode}/users/{userUid}'],
            idParam: 'userUid',
        },
        // ['users', org] prefix-catches every useUsers() filter variant
        // (api/queries.ts:311) — the list page, its expanded-children rows,
        // and the detail page's own children query all key off this same
        // prefix. ['userAncestors', org] is deliberately collection-level
        // (not detail-keyed) because the ancestor-chain hook
        // (routes/$org/users/$userUid/index.tsx:40) keys on the CURRENT
        // user's parent_user_uid, an arbitrary ancestor's uid a hint can't
        // name — so a users hint invalidates every mounted ancestor-chain
        // query for the org rather than trying to target one.
        manualKeyPrefixes: (org) => [
            ['users', org],
            ['userAncestors', org],
        ],
        // A users:<uid> hint's uid matches useUser's key (api/queries.ts:364)
        // exactly.
        detailManualKeyPrefixes: (org, uid) => [['user', org, uid]],
    },
    // jobs has NO openapi-fetch-shaped queries at all — front/src/api/jobs.ts
    // hand-rolls every query key (`['jobs', org, params]`, `['job', org,
    // jobUid]`, `['jobExecution', org, jobUid, executionUid, logsLimit]`,
    // `['jobsStatus', org]`, `['jobsBlockers', org]`), so `collection` is empty
    // and both prefix mechanisms below do all the work.
    jobs: {
        collection: ['/api/v1/organizations/{orgCode}/imports/{importUid}/summary'],
        manualKeyPrefixes: (org) => [
            ['jobs', org],
            ['jobsStatus', org],
            // Processing blockers (spec 2026-08-18-01): resolving a proposal
            // re-enqueues the paused backlog, which dirties the jobs scope —
            // so the blockers list drops the resolved row without a reload.
            ['jobsBlockers', org],
        ],
        // A jobs:<uid> hint's uid is always a JOB's uid (never an
        // execution's — see migration 094's parent-uid resolution in
        // docs/apis/events.md), so ['job', org, uid] matches useJob's key
        // exactly and ['jobExecution', org, uid] — a PREFIX of
        // ['jobExecution', org, jobUid, executionUid, logsLimit] — catches
        // every execution/logsLimit variant for that job in one predicate,
        // without needing to know the execution uid.
        detailManualKeyPrefixes: (org, uid) => [
            ['job', org, uid],
            ['jobExecution', org, uid],
        ],
    },
    // agent-runs (spec 2026-10-21-19 §3): an agent_runs hint carries the
    // run's uid, an agent_run_events hint its parent run's uid, so the detail
    // scope refreshes the card, the run page and its events and LLM calls.
    'agent-runs': {
        collection: ['/api/v1/organizations/{orgCode}/agent-runs'],
        detail: {
            paths: [
                '/api/v1/organizations/{orgCode}/agent-runs/{agentRunUid}',
                '/api/v1/organizations/{orgCode}/agent-runs/{agentRunUid}/events',
                '/api/v1/organizations/{orgCode}/agent-runs/{agentRunUid}/llm-calls',
            ],
            idParam: 'agentRunUid',
        },
    },
    // assistant-threads (spec 2026-10-21-24 §11): a thread's messages and its
    // runs' events publish the thread uid; detail hints reach the author only.
    'assistant-threads': {
        collection: ['/api/v1/organizations/{orgCode}/assistant/threads'],
        detail: {
            paths: ['/api/v1/organizations/{orgCode}/assistant/threads/{uid}'],
            idParam: 'uid',
        },
    },
    // properties is deliberately coarse (collection-only, no detail scope):
    // a property DEFINITION change is rare and org-wide by nature (it's
    // metadata, not per-asset data), so any hint just invalidates every
    // mounted property-definition/enum-value query for the org. Cheap
    // because these queries are already org-scoped and infrequently
    // mounted compared to assets/documents.
    properties: {
        collection: [
            '/api/v1/organizations/{orgCode}/properties',
            '/api/v1/organizations/{orgCode}/properties/{propertyUid}',
            '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values',
            '/api/v1/organizations/{orgCode}/properties/{propertyUid}/usage',
        ],
    },
}

/**
 * invalidateForScope invalidates the TanStack Query cache entries mapped to
 * one "update" hint. Called from LiveEventsContext's onFrame handler.
 */
export function invalidateForScope(queryClient: QueryClient, org: string, scope: LiveScope) {
    const mapping = ENTITY_PATHS[scope.entity]
    if (!mapping) return // not a whitelisted entity — should never happen if the server enforces the same list, but harmless if it does

    for (const path of mapping.collection) {
        invalidateOrgScoped(queryClient, path, org)
    }

    if (scope.uid && mapping.detail) {
        for (const path of mapping.detail.paths) {
            invalidateOrgAndKeyScoped(queryClient, path, org, mapping.detail.idParam, scope.uid)
        }
    }

    for (const keyPrefix of mapping.manualKeyPrefixes?.(org) ?? []) {
        queryClient.invalidateQueries({ queryKey: keyPrefix })
    }

    if (scope.uid) {
        for (const keyPrefix of mapping.detailManualKeyPrefixes?.(org, scope.uid) ?? []) {
            queryClient.invalidateQueries({ queryKey: keyPrefix })
        }
    }
}

/**
 * invalidateEverySubscribedScope invalidates the cache for every scope in
 * `scopes` — used on a "resync" frame, which tells the client to re-fetch
 * everything it currently subscribes to (see docs/apis/events.md's "Resync
 * semantics").
 */
export function invalidateEverySubscribedScope(queryClient: QueryClient, org: string, scopes: Iterable<LiveScope>) {
    for (const scope of scopes) {
        invalidateForScope(queryClient, org, scope)
    }
}

// Trailing-edge window (ms) for coalescing per-scope live-update
// invalidations. The server enforces a 250 ms minimum flush gap
// (Realtime.FlushMinGap), so a continuously-dirty collection scope would emit
// an `update` frame every ~250 ms — and, undebounced, drive ~4 whole-list
// refetches/second on the assets list page. This window collapses such a burst
// into a single refetch, matching the stated intent at
// front/src/routes/$org/assets/index.tsx ("refetch the list within ~1-2s of
// any asset change"). See
// specs/todos/2026-07-09-04-frontend-assets-live-refetch-storm.md (step 3).
export const SCOPE_INVALIDATION_DEBOUNCE_MS = 1000

// debounceKeyFor mirrors subscription-registry's scopeKey: a scope with no uid
// (a collection-scope hint) keys on the entity alone, so the storm of
// collection-scope `update` frames the spec describes all share one key and
// coalesce; a detail-scope hint keys on entity+uid so distinct assets keep
// independent timers.
function debounceKeyFor(scope: LiveScope): string {
    return scope.uid !== undefined ? `${scope.entity}:${scope.uid}` : scope.entity
}

/**
 * ScopeInvalidationDebouncer coalesces a burst of live "update" frames for the
 * same scope into a single trailing-edge cache invalidation. Each distinct
 * scope key (entity, or entity+uid) gets its own timer, so a busy `assets`
 * collection scope never starves a quiet `documents` one — and a single
 * isolated frame still triggers exactly one invalidation, after the window (no
 * burst is required). One instance is owned per LiveEventsProvider connection;
 * call dispose() on teardown so no timer fires an invalidation against a stale
 * QueryClient after unmount.
 */
export class ScopeInvalidationDebouncer {
    private readonly timers = new Map<string, ReturnType<typeof setTimeout>>()
    private readonly pending = new Map<string, LiveScope>()
    private readonly queryClient: QueryClient
    private readonly org: string
    private readonly windowMs: number

    constructor(
        queryClient: QueryClient,
        org: string,
        windowMs: number = SCOPE_INVALIDATION_DEBOUNCE_MS,
    ) {
        this.queryClient = queryClient
        this.org = org
        this.windowMs = windowMs
    }

    // schedule registers `scope` for a trailing-edge invalidation. Repeated
    // calls for the same key within the window reset that key's timer (so a
    // steady stream never actually fires until it pauses for windowMs), while
    // keeping the LATEST scope object for the key — a later detail hint thus
    // supersedes an earlier one for the same entity+uid.
    schedule(scope: LiveScope): void {
        const key = debounceKeyFor(scope)
        this.pending.set(key, scope)
        const existing = this.timers.get(key)
        if (existing !== undefined) clearTimeout(existing)
        this.timers.set(
            key,
            setTimeout(() => {
                this.timers.delete(key)
                const pendingScope = this.pending.get(key)
                this.pending.delete(key)
                if (pendingScope !== undefined) {
                    invalidateForScope(this.queryClient, this.org, pendingScope)
                }
            }, this.windowMs),
        )
    }

    // dispose clears every pending timer WITHOUT flushing — used on provider
    // unmount / socket teardown.
    dispose(): void {
        for (const timer of this.timers.values()) clearTimeout(timer)
        this.timers.clear()
        this.pending.clear()
    }
}
